import React, { useState } from 'react';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  onValidate?: (value: string) => string | undefined;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      onValidate,
      validateOnBlur = true,
      validateOnChange = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const [internalError, setInternalError] = useState<string | undefined>();

    // Gerar IDs únicos para acessibilidade
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${textareaId}-error`;
    const helperId = `${textareaId}-helper`;

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (validateOnBlur && onValidate) {
        const validationError = onValidate(e.target.value);
        setInternalError(validationError);
      }
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (validateOnChange && onValidate) {
        const validationError = onValidate(e.target.value);
        setInternalError(validationError);
      }
      props.onChange?.(e);
    };

    const displayError = error || internalError;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-semibold text-neutral-700 mb-1.5"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          aria-invalid={!!displayError}
          aria-describedby={
            displayError ? errorId : helperText ? helperId : undefined
          }
          className={`
            w-full px-4 py-2.5
            bg-white border rounded-lg
            shadow-sm
            transition-all duration-150
            focus:outline-none focus:ring-2
            placeholder:text-neutral-400
            resize-vertical
            ${
              displayError
                ? 'border-danger-500 focus:ring-danger-200 focus:border-danger-500 bg-danger-50'
                : 'border-neutral-200 focus:ring-primary-200 focus:border-primary-500'
            }
            ${className}
          `}
          onBlur={handleBlur}
          onChange={handleChange}
          {...props}
        />
        {displayError && (
          <p
            id={errorId}
            className="mt-1.5 text-sm text-danger-600 flex items-start gap-1"
            role="alert"
          >
            <span className="mt-0.5">⚠</span>
            <span>{displayError}</span>
          </p>
        )}
        {helperText && !displayError && (
          <p id={helperId} className="mt-1.5 text-xs text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
